import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  CardActionArea,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useSiteSetting, useUpdateSiteSetting } from "../../hooks/useSiteSettings";
import { useImages, Image } from "../../hooks/useImages";
import { getImageId } from "../../utils/imageUtils";
import { Pagination } from "./Pagination";
import { ImageCropperModal } from "./ImageCropperModal";

interface HeroImageSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

export const HeroImageSettingsModal: React.FC<HeroImageSettingsModalProps> = ({
  open,
  onClose,
}) => {
  const [selectedImage, setSelectedImage] = useState<Image | null>(null);
  const [page, setPage] = useState(1);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const { data: setting, isLoading: settingLoading } = useSiteSetting(open ? "hero_image_url" : null);
  const { data: imagesData, isLoading: imagesLoading } = useImages({
    page: page,
    page_size: 50,
    sort_by: "upload_date",
    sort_order: "desc",
  });
  const updateSetting = useUpdateSiteSetting();

  // Update local state when setting loads
  useEffect(() => {
    if (setting) {
      // Try to find if the current URL matches an uploaded image
      const matchingImage = imagesData?.items?.find(
        (img) => img.cloudinary_url === setting.value
      );
      if (matchingImage) {
        setSelectedImage(matchingImage);
      }
    }
  }, [setting, imagesData]);

  // Reset page when modal opens
  useEffect(() => {
    if (open) {
      setPage(1);
    }
  }, [open]);

  const handleSave = async () => {
    if (!selectedImage) {
      return;
    }

    try {
      await updateSetting.mutateAsync({
        key: "hero_image_url",
        data: {
          value: selectedImage.cloudinary_url,
          is_public: true, // Ensure it's public so it appears on the homepage
        },
      });
      onClose();
    } catch (error) {
      // Error handling is done by the hook
    }
  };

  const handleClose = () => {
    if (!updateSetting.isPending) {
      // Reset to original value if not saved
      if (setting) {
        const matchingImage = imagesData?.items?.find(
          (img) => img.cloudinary_url === setting.value
        );
        setSelectedImage(matchingImage || null);
      }
      onClose();
    }
  };

  const handleImageSelect = (image: Image) => {
    setSelectedImage(image);
  };

  const handleCropComplete = (croppedImageUrl: string) => {
    // After cropping, find the newly created image in the database
    // The cropped image is uploaded to Cloudinary and should appear in the images list
    // For now, we'll need to refresh the images list or wait for it to update
    // Since the cropped image is uploaded as a new image, we should select it
    // This will be handled by the ImageCropperModal which uploads the cropped image
    // and it should appear in the images list after a refresh
    const croppedImage = imagesData?.items?.find(
      (img) => img.cloudinary_url === croppedImageUrl
    );
    if (croppedImage) {
      setSelectedImage(croppedImage);
    }
  };

  const currentPreviewUrl = selectedImage?.cloudinary_url;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>Change Hero Image</DialogTitle>
      <DialogContent
        sx={{
          overflowY: 'auto',
          maxHeight: 'calc(90vh - 140px)',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.3)',
            },
          },
        }}
      >
        {settingLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select an image from your uploaded images to use as the hero image.
            </Typography>

                {imagesLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : imagesData?.items && imagesData.items.length > 0 ? (
                  <Box>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, 1fr)",
                          md: "repeat(3, 1fr)",
                          lg: "repeat(4, 1fr)",
                        },
                        gap: 2,
                      }}
                    >
                      {imagesData.items.map((image) => {
                        const imageId = getImageId(image);
                        const isSelected = selectedImage?.id === image.id;
                        const thumbnailUrl = image.cloudinary_url.replace(
                          "/upload/",
                          "/upload/w_400,h_300,c_fill/"
                        );

                        return (
                          <Box key={imageId}>
                            <Card
                              sx={{
                                position: "relative",
                                border: isSelected ? 2 : 1,
                                borderColor: isSelected ? "primary.main" : "divider",
                                cursor: "pointer",
                                transition: "all 0.2s",
                                "&:hover": {
                                  transform: "translateY(-4px)",
                                  boxShadow: 3,
                                },
                              }}
                            >
                              <CardActionArea onClick={() => handleImageSelect(image)}>
                                <CardMedia
                                  component="img"
                                  image={thumbnailUrl}
                                  alt={image.title || "Image"}
                                  sx={{
                                    height: 150,
                                    objectFit: "cover",
                                  }}
                                />
                                {isSelected && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: 8,
                                      right: 8,
                                      backgroundColor: "primary.main",
                                      borderRadius: "50%",
                                      p: 0.5,
                                    }}
                                  >
                                    <CheckCircleIcon sx={{ color: "white", fontSize: 20 }} />
                                  </Box>
                                )}
                              </CardActionArea>
                            </Card>
                            {image.title && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  mt: 0.5,
                                  textAlign: "center",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {image.title}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                    {imagesData.total > 50 && (
                      <Pagination
                        page={page}
                        pageSize={50}
                        total={imagesData.total}
                        onPageChange={setPage}
                      />
                    )}
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No images found. Upload some images first to use them as hero images.
                  </Alert>
                )}

            {currentPreviewUrl && (
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Preview:
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setCropModalOpen(true)}
                    disabled={!currentPreviewUrl}
                  >
                    Crop Image
                  </Button>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    height: 300,
                    borderRadius: 1,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    position: "relative",
                  }}
                >
                  <Box
                    component="img"
                    src={currentPreviewUrl}
                    alt="Hero preview"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      // Show error if image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                    }}
                  />
                </Box>
              </Box>
            )}

            {updateSetting.isError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Failed to update hero image. Please try again.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={updateSetting.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!selectedImage || updateSetting.isPending}
        >
          {updateSetting.isPending ? <CircularProgress size={20} /> : "Save"}
        </Button>
      </DialogActions>

      {/* Image Cropper Modal */}
      <ImageCropperModal
        open={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        imageUrl={currentPreviewUrl || ""}
        imageTitle="Hero Image"
        aspectRatio={16 / 9}
        onCropComplete={handleCropComplete}
      />
    </Dialog>
  );
};
